import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AuthPageShell, DoctorCard, Button, RrnInput } from '@/components';
import { useAuthFetch, usePractice } from '@/hooks';
import { validateRRN, doctorPortraitUrl, getDoctorInitials } from '@/utils';
import { ROUTES, practiceConversationPath } from '../../routes.ts';
import {
  sexFromRRN,
  birthDateFromRRN,
  INTAKE_INFORMED_CONSENT_VERSION,
  MEDICAL_DISCLAIMER_VERSION,
  PRIVACY_POLICY_VERSION,
  USER_AGREEMENT_VERSION,
} from '@dermalink/shared';
import type { ConversationSubject, DoctorListItem, StartConversationResponse, UserProfileResponse } from '@dermalink/shared';
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, UserIcon, UsersIcon } from '@/components/icons.tsx';
import styles from './Intake.module.css';

function ageFromIso(iso: string): number {
  const today = new Date();
  const birth = new Date(iso);
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
  return age;
}

function IntakeDoctorAvatar({ doctor, className }: { doctor: DoctorListItem; className: string }) {
  const portrait = doctorPortraitUrl(doctor);
  return portrait
    ? <img src={portrait} alt="" className={`${styles.intakePhoto} ${className}`} />
    : <span className={`${styles.intakeAvatar} ${className}`} aria-hidden="true">{getDoctorInitials(doctor)}</span>;
}

function IntakeDoctorHeader({ doctor }: { doctor: DoctorListItem }) {
  return (
    <div className={styles.intakeDoctorHeader}>
      <IntakeDoctorAvatar doctor={doctor} className={styles.intakeAvatarLg} />
      <span className={styles.intakeDoctorName}>{`Dr. ${doctor.first_name ?? ''} ${doctor.last_name ?? ''}`.trim()}</span>
    </div>
  );
}

function IntakeQuestionRow({ doctor, children }: { doctor: DoctorListItem; children: React.ReactNode }) {
  return (
    <div className={styles.intakeRow}>
      <IntakeDoctorAvatar doctor={doctor} className={styles.intakeAvatarSm} />
      <p className={styles.intakeBubble}>{children}</p>
    </div>
  );
}

function IntakeAnswerRow({ answer, onEdit }: { answer: string; onEdit: () => void }) {
  return (
    <div className={styles.intakeAnswerRow}>
      <button type="button" className={styles.intakeEdit} onClick={onEdit}>Wijzig</button>
      <p className={styles.intakeAnswerBubble}>{answer || '—'}</p>
    </div>
  );
}

function ConsentItem({ title, checked, onChange, children }: { title?: string; checked: boolean; onChange: (checked: boolean) => void; children: React.ReactNode }) {
  return (
    <div className={`${styles.consentItem}${checked ? ` ${styles.consentItemChecked}` : ''}`}>
      <label className={styles.consentCheck}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className={title ? styles.consentItemTitle : undefined}>{title ?? children}</span>
      </label>
      {title && (
        <details className={styles.consentDetails}>
          <summary>Volledige tekst<ChevronDownIcon size={12} /></summary>
          <p>{children}</p>
        </details>
      )}
    </div>
  );
}

// Dutch possessive: names ending in s, x or z take an apostrophe (Thomas'), others an s (Lottes).
function possessive(name: string) {
  return /[sxz]$/i.test(name) ? `${name}'` : `${name}s`;
}

const STEP_LABELS = ['Voor wie', 'Dermatoloog', 'Vragen', 'Tarief', 'Toestemming'];

function YesNoRadioGroup({ name, value, onChange }: { name: string; value: 'yes' | 'no' | ''; onChange: (value: 'yes' | 'no') => void }) {
  return (
    <div className={styles.intakeRadios}>
      <label className={styles.intakeRadio}>
        <input type="radio" name={name} checked={value === 'yes'} onChange={() => onChange('yes')} />
        Ja
      </label>
      <label className={styles.intakeRadio}>
        <input type="radio" name={name} checked={value === 'no'} onChange={() => onChange('no')} />
        Nee
      </label>
    </div>
  );
}

export function Intake() {
  const navigate  = useNavigate();
  const authFetch = useAuthFetch();
  const { slug: practiceSlug, practice } = usePractice();

  // undefined = step 1 not completed yet; null = for myself; object = for a subject
  const [subject, setSubject] = useState<ConversationSubject | null | undefined>(undefined);
  const [subjectMode, setSubjectMode] = useState<'self' | 'other' | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [rrn, setRrn] = useState('');
  const [rrnError, setRrnError] = useState('');

  const [selectedDoctor, setSelectedDoctor] = useState<DoctorListItem | null>(null);
  const [step3Done, setStep3Done] = useState(false);
  /** Stap 3: toon één artsvraag tegelijk (1 → 2 → 3 → 4). */
  const [intakeSubStep, setIntakeSubStep] = useState<1 | 2 | 3 | 4>(1);
  const [pregnant, setPregnant] = useState<'yes' | 'no' | ''>('');
  const [medication, setMedication] = useState('');
  const [allergies, setAllergies] = useState('');
  const [recentConsult, setRecentConsult] = useState<'yes' | 'no' | ''>('');
  const [showIntakeConsentStep, setShowIntakeConsentStep] = useState(false);
  const [consentNoEmergency, setConsentNoEmergency] = useState(false);
  const [consentNoReimbursement, setConsentNoReimbursement] = useState(false);
  const [consentHealthData, setConsentHealthData] = useState(false);
  const [consentLegalDocs, setConsentLegalDocs] = useState(false);
  const [consentOnBehalfOf, setConsentOnBehalfOf] = useState(false);

  const { data, isPending, isError } = useQuery<DoctorListItem[]>({
    queryKey: ['doctors'],
    queryFn: async () => {
      const res = await authFetch('/doctors');
      if (!res.ok) throw new Error();
      return (await res.json()) as DoctorListItem[];
    },
  });
  const doctors: DoctorListItem[] = (data ?? []).filter((d) => d.organization_slug === practiceSlug);
  const practiceUnavailable = practice?.accepts_patients === false || (!isPending && !isError && doctors.length === 0);

  const { data: profileData } = useQuery<UserProfileResponse | null>({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await authFetch('/profile');
      if (res.status === 404) return null;
      if (!res.ok) throw new Error();
      return (await res.json()) as UserProfileResponse;
    },
  });
  const effectiveRrn = subjectMode === 'other' && subject != null ? subject.rrn : (profileData?.rrn ?? '');
  const effectiveBirthDate = birthDateFromRRN(effectiveRrn);
  const skipPregnancy = sexFromRRN(effectiveRrn) === 'male' || (effectiveBirthDate !== null && ageFromIso(effectiveBirthDate) < 10);
  const subjectName = subject?.first_name ?? null;

  const startMutation = useMutation({
    mutationFn: async ({
      doctorSub,
      followUpNoPayment,
      informedConsent,
    }: {
      doctorSub: string;
      followUpNoPayment: boolean;
      informedConsent: {
        no_emergency_confirmation: boolean;
        no_reimbursement_confirmation: boolean;
        health_data_processing_consent: boolean;
        legal_docs_confirmation: boolean;
        on_behalf_of_confirmation: boolean;
      };
    }) => {
      const res = await authFetch('/conversations', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          doctor_cognito_sub: doctorSub,
          organization_slug: practiceSlug,
          subject: subject ?? undefined,
          intake_pregnant: pregnant || undefined,
          intake_medication: medication.trim() || undefined,
          intake_allergies: allergies.trim() || undefined,
          follow_up_recent_consult: followUpNoPayment,
          informed_consent: {
            version: INTAKE_INFORMED_CONSENT_VERSION,
            medical_disclaimer_version: MEDICAL_DISCLAIMER_VERSION,
            user_agreement_version: USER_AGREEMENT_VERSION,
            privacy_policy_version: PRIVACY_POLICY_VERSION,
            ...informedConsent,
          },
        }),
      });
      if (!res.ok) {
        let detail = '';
        try {
          const body = (await res.json()) as { message?: string };
          if (typeof body.message === 'string' && body.message.trim()) detail = body.message.trim();
        } catch { /* ignore */ }
        throw new Error(detail || `Kon geen gesprek starten (${res.status}).`);
      }
      return res.json() as Promise<StartConversationResponse>;
    },
    onSuccess: (data) => {
      navigate(practiceConversationPath(practiceSlug, data.conversation_id));
    },
  });

  function chooseSelf() {
    setSubjectMode('self');
    setRrnError('');
    setSubject(null);
  }

  function handleSubjectConfirm(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!validateRRN(rrn)) {
      setRrnError('Ongeldig rijksregisternummer.');
      return;
    }
    setRrnError('');
    setSubject({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      rrn: rrn.replace(/\D/g, ''),
    });
  }

  function handlePickDoctor(cognitoSub: string) {
    const doc = doctors.find((d) => d.cognito_sub === cognitoSub) ?? null;
    setSelectedDoctor(doc);
    setStep3Done(false);
    setIntakeSubStep(1);
    setPregnant('');
    setMedication('');
    setAllergies('');
    setRecentConsult('');
    setShowIntakeConsentStep(false);
    setConsentNoEmergency(false);
    setConsentNoReimbursement(false);
    setConsentHealthData(false);
    setConsentLegalDocs(false);
    setConsentOnBehalfOf(false);
  }

  const step1Done     = subject !== undefined;
  const doctorChosen  = selectedDoctor !== null;
  const intakePregnantOk = pregnant === 'yes' || pregnant === 'no';
  const intakeRecentOk = recentConsult === 'yes' || recentConsult === 'no';
  const isFollowUpNoPayment = recentConsult === 'yes';
  const allIntakeConsentsChecked =
    consentNoEmergency
    && consentNoReimbursement
    && consentHealthData
    && consentLegalDocs
    && (subject == null || consentOnBehalfOf);
  const activeStep = !step1Done ? 1 : !doctorChosen ? 2 : !step3Done ? 3 : !showIntakeConsentStep ? 4 : 5;
  const subjectLine = subject ? `Voor ${subject.first_name} ${subject.last_name}` : 'Voor mezelf';
  const stepSubs = [
    step1Done ? subjectLine : null,
    selectedDoctor ? `Dr. ${selectedDoctor.first_name ?? ''} ${selectedDoctor.last_name ?? ''}`.trim() : null,
  ];
  const yesNo = (v: 'yes' | 'no' | '') => (v === 'yes' ? 'Ja' : v === 'no' ? 'Nee' : '');

  const questions: { n: 1 | 2 | 3 | 4; text: React.ReactNode; answer: string }[] = [
    {
      n: 1,
      text: subjectName
        ? <>Hallo! Voor we starten: <strong>neemt {subjectName} medicatie</strong>? Zo ja, wat?</>
        : <>Hallo! Voor we starten: <strong>neemt u medicatie</strong>? Zo ja, wat neemt u?</>,
      answer: medication.trim(),
    },
    {
      n: 2,
      text: subjectName
        ? <><strong>Is {subjectName} allergisch</strong> voor bepaalde producten of medicijnen?</>
        : <><strong>Bent u allergisch</strong> voor bepaalde producten of medicijnen?</>,
      answer: allergies.trim(),
    },
    {
      n: 3,
      text: subjectName
        ? <>Is {subjectName} voor <strong>ditzelfde probleem</strong> in <strong>de laatste 30 dagen</strong> al bij ons op consultatie geweest?</>
        : <>Ben je voor <strong>ditzelfde probleem</strong> in <strong>de laatste 30 dagen</strong> al bij ons op consultatie geweest?</>,
      answer: yesNo(recentConsult),
    },
    ...(skipPregnancy ? [] : [{
      n: 4 as const,
      text: subjectName
        ? <>Tot slot: <strong>is {subjectName} zwanger</strong> of denkt u dat {subjectName} zwanger is?</>
        : <>Tot slot: <strong>bent u zwanger</strong> of denkt u dat u zwanger bent?</>,
      answer: yesNo(pregnant),
    }]),
  ];
  const currentQuestion = questions.find((q) => q.n === intakeSubStep);

  return (
    <AuthPageShell maxWidth="1080px" padTop="16px" patientBackdrop practice={practice}>
      <div className={styles.layout}>
        {!practiceUnavailable && (
          <ol className={styles.stepper} aria-label="Stappen">
            {STEP_LABELS.map((label, i) => {
              const n = i + 1;
              const state = n < activeStep ? styles.stepDone : n === activeStep ? styles.stepCurrent : '';
              return (
                <li key={label} className={`${styles.step} ${state}`} aria-current={n === activeStep ? 'step' : undefined}>
                  <span className={styles.stepDot}>{n < activeStep ? <CheckIcon size={14} /> : n}</span>
                  <span className={styles.stepLabel}>
                    {label}
                    {n < activeStep && stepSubs[i] && <small className={styles.stepSub}>{stepSubs[i]}</small>}
                  </span>
                </li>
              );
            })}
          </ol>
        )}

        <div className={styles.pageCard}>
          <h1 className="page-heading">
            Teleconsultatie starten{practice && <> bij <strong>{practice.name}</strong></>}
          </h1>
          <p className={`text-muted ${styles.subheading}`}>
            Volg de stappen hieronder om je consultatie op te starten.
          </p>

          {practiceUnavailable && (
            <p className={`status-message ${styles.deeplinkNotice}`} role="alert">
              Deze praktijk biedt momenteel geen teleconsultaties aan via Dermalink.{' '}
              <Link to={ROUTES.practices}>Kies een andere praktijk</Link>.
            </p>
          )}

          {!practiceUnavailable && (
            <div
              className={styles.mobileProgress}
              role="progressbar"
              aria-valuemin={1}
              aria-valuemax={5}
              aria-valuenow={activeStep}
              aria-valuetext={`Stap ${activeStep} van 5`}
            >
              <div className={styles.mobileProgressLabel}>
                <strong>Stap {activeStep} van 5</strong>
                <span>{STEP_LABELS[activeStep - 1]}</span>
              </div>
              <div className={styles.mobileProgressBar}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <span key={n} className={`${styles.mobileProgressSeg}${n <= activeStep ? ` ${styles.mobileProgressSegDone}` : ''}`} />
                ))}
              </div>
            </div>
          )}

          {!practiceUnavailable && activeStep === 1 && (
            <section className={styles.stepSection}>
              <header className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Stap 1: Voor wie is deze consultatie?</h2>
              </header>
              <p className={styles.sectionText}>
                Start je dit gesprek voor jezelf, voor je kind of voor iemand anders waarvoor je zorgt?
              </p>
              <div className={styles.choiceList}>
                <button type="button" className={styles.choice} onClick={chooseSelf}>
                  <span className={styles.choiceIcon}><UserIcon size={20} /></span>
                  <span className={styles.choiceTitle}>Voor mezelf</span>
                  <ChevronRightIcon size={16} className={styles.choiceChevron} />
                </button>
                <button
                  type="button"
                  className={`${styles.choice}${subjectMode === 'other' ? ` ${styles.choiceSelected}` : ''}`}
                  aria-pressed={subjectMode === 'other'}
                  onClick={() => {
                    setSubjectMode('other');
                    setSubject(undefined);
                  }}
                >
                  <span className={styles.choiceIcon}><UsersIcon size={20} /></span>
                  <span className={styles.choiceTitle}>Voor iemand anders</span>
                  <ChevronRightIcon size={16} className={styles.choiceChevron} />
                </button>
              </div>
              {subjectMode === 'other' && (
                <form className={styles.subjectForm} onSubmit={handleSubjectConfirm}>
                  <div className="field-group">
                    <label className="field-label" htmlFor="subject-first-name">Voornaam</label>
                    <input
                      id="subject-first-name"
                      className="field-input"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      autoComplete="off"
                    />
                  </div>
                  <div className="field-group">
                    <label className="field-label" htmlFor="subject-last-name">Naam</label>
                    <input
                      id="subject-last-name"
                      className="field-input"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      autoComplete="off"
                    />
                  </div>
                  <RrnInput
                    id="subject-rrn"
                    value={rrn}
                    onChange={(v) => { setRrn(v); setRrnError(''); }}
                    error={rrnError}
                  />
                  <div className={styles.subjectActions}>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!firstName.trim() || !lastName.trim() || rrn.replace(/\D/g, '').length < 11}
                    >
                      Gegevens bevestigen
                    </Button>
                  </div>
                </form>
              )}
            </section>
          )}

          {!practiceUnavailable && activeStep === 2 && (
            <section className={styles.stepSection}>
              <header className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Stap 2: Kies je dermatoloog</h2>
              </header>
              <p className={styles.sectionText}>
                Kies de dermatoloog{practice && <> van {practice.name}</>} waarmee u dit teleconsult wilt starten.
              </p>
              <div className={styles.subjectSummary}>
                <span>{subjectLine}</span>
                <button type="button" className={styles.intakeEdit} onClick={() => setSubject(undefined)}>Wijzig</button>
              </div>
              {isPending && <p className="text-muted status-message">Laden&hellip;</p>}
              {isError && <p className="text-muted status-message">Kon artsenlijst niet laden.</p>}
              {doctors.length > 0 && (
                <div className={styles.grid}>
                  {doctors.map((doc) => (
                    <DoctorCard
                      key={doc.cognito_sub}
                      doctor={doc}
                      onSelect={handlePickDoctor}
                      selected={false}
                      disabled={startMutation.isPending}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {!practiceUnavailable && activeStep === 3 && selectedDoctor && currentQuestion && (
            <section className={styles.stepSection}>
              <header className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Stap 3: Extra info</h2>
              </header>
              <IntakeDoctorHeader doctor={selectedDoctor} />
              <div className={styles.intakeThread}>
                {questions.filter((q) => q.n < intakeSubStep).map((q) => (
                  <div key={q.n} className={styles.intakeExchange}>
                    <IntakeQuestionRow doctor={selectedDoctor}>{q.text}</IntakeQuestionRow>
                    <IntakeAnswerRow answer={q.answer} onEdit={() => setIntakeSubStep(q.n)} />
                  </div>
                ))}
                <IntakeQuestionRow doctor={selectedDoctor}>{currentQuestion.text}</IntakeQuestionRow>
              </div>

              {intakeSubStep === 1 && (
                <>
                  <div className={styles.intakeAnswer}>
                    <span className={styles.intakeLabel}>Uw antwoord</span>
                    <textarea
                      className={styles.intakeText}
                      rows={2}
                      value={medication}
                      onChange={(e) => setMedication(e.target.value)}
                      placeholder="Bijv. geen, of naam en dosering"
                    />
                  </div>
                  <div className={styles.step3Actions}>
                    <Button type="button" variant="primary" onClick={() => setIntakeSubStep(2)}>Volgende vraag</Button>
                  </div>
                </>
              )}

              {intakeSubStep === 2 && (
                <>
                  <div className={styles.intakeAnswer}>
                    <span className={styles.intakeLabel}>Uw antwoord</span>
                    <textarea
                      className={styles.intakeText}
                      rows={2}
                      value={allergies}
                      onChange={(e) => setAllergies(e.target.value)}
                      placeholder="Bijv. nee, of beschrijf kort"
                    />
                  </div>
                  <div className={styles.step3Actions}>
                    <Button type="button" variant="ghost" onClick={() => setIntakeSubStep(1)}>
                      Terug
                    </Button>
                    <Button type="button" variant="primary" onClick={() => setIntakeSubStep(3)}>
                      Volgende vraag
                    </Button>
                  </div>
                </>
              )}

              {intakeSubStep === 3 && (
                <>
                  <div className={styles.intakeAnswer}>
                    <span className={styles.intakeLabel}>Uw antwoord</span>
                    <YesNoRadioGroup name="recent-consult" value={recentConsult} onChange={setRecentConsult} />
                  </div>
                  <div className={styles.step3Actions}>
                    <Button type="button" variant="ghost" onClick={() => setIntakeSubStep(2)}>
                      Terug
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={!intakeRecentOk}
                      onClick={() => (skipPregnancy ? setStep3Done(true) : setIntakeSubStep(4))}
                    >
                      {skipPregnancy ? 'Volgende stap' : 'Volgende vraag'}
                    </Button>
                  </div>
                </>
              )}

              {intakeSubStep === 4 && !skipPregnancy && (
                <>
                  <div className={styles.intakeAnswer}>
                    <span className={styles.intakeLabel}>Uw antwoord</span>
                    <YesNoRadioGroup name="pregnant" value={pregnant} onChange={setPregnant} />
                  </div>
                  <div className={styles.step3Actions}>
                    <Button type="button" variant="ghost" onClick={() => setIntakeSubStep(3)}>
                      Terug
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={!intakePregnantOk}
                      onClick={() => setStep3Done(true)}
                    >
                      Volgende stap
                    </Button>
                  </div>
                </>
              )}
            </section>
          )}

          {!practiceUnavailable && activeStep === 4 && (
            <section className={styles.stepSection}>
              <header className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Stap 4: Tarief</h2>
              </header>
              <ul className={styles.tariffList} aria-label="Mogelijke tarieven">
                <li className={styles.tariffRow}>
                  <span className={styles.tariffHeaderRow}>
                    <span className={styles.tariffLabel}>Opvolging</span>
                    <span className={styles.tariffPrice}>€ 0</span>
                  </span>
                  <span className={styles.tariffDesc}>Je hebt voor dit probleem in de laatste 30 dagen al een fysieke of digitale consultatie gehad bij je dermatoloog.</span>
                </li>
                <li className={styles.tariffRow}>
                  <span className={styles.tariffHeaderRow}>
                    <span className={styles.tariffLabel}>Enkel voorschrift</span>
                    <span className={styles.tariffPrice}>€ 5</span>
                  </span>
                  <span className={styles.tariffDesc}>Je hebt een voorschrift nodig voor een probleem dat reeds wordt opgevolgd bij je dermatoloog.</span>
                </li>
                <li className={styles.tariffRow}>
                  <span className={styles.tariffHeaderRow}>
                    <span className={styles.tariffLabel}>Nieuw probleem</span>
                    <span className={styles.tariffPrice}>€ 32</span>
                  </span>
                  <span className={styles.tariffDesc}>Voor dit probleem heb je de laatste 30 dagen geen consultatie gehad.</span>
                </li>
              </ul>
              <div className={styles.agreementNotice} role="note" aria-label="Akkoordverklaring">
                <span className={styles.agreementIcon} aria-hidden="true">!</span>
                <span>Door het gesprek te starten ga je akkoord met deze tariefafspraak.</span>
              </div>
              <p className={`${styles.sectionText} ${styles.step4InfoText}`}>
                Je gesprek start zo meteen. De prijzen hierboven zijn een richtlijn: de dermatoloog bepaalt pas na het lezen van je bericht welk tarief van toepassing is, en dat kan afwijken van wat je zelf verwachtte.
              </p>
              <p className={`${styles.sectionText} ${styles.step4InfoText}`}>
                Beschrijf in dit gesprek <strong>één huidprobleem</strong>; voor een ander probleem start je een nieuw teleconsult.
              </p>
              <div className={styles.step4Actions}>
                <Button type="button" variant="ghost" onClick={() => setStep3Done(false)}>
                  Terug
                </Button>
                <Button type="button" variant="primary" onClick={() => setShowIntakeConsentStep(true)}>
                  Volgende
                </Button>
              </div>
            </section>
          )}

          {!practiceUnavailable && activeStep === 5 && selectedDoctor && (
            <section className={styles.stepSection}>
              <header className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Nog even dit, voor u start</h2>
              </header>
              <div className={styles.consentCard}>
                <p className={styles.sectionText}>
                  U hebt zo meteen de mogelijkheid om foto&apos;s en een klinische vraag te delen met een dermatoloog van {selectedDoctor.organization_name}, die u op afstand een advies kan geven.
                  Omdat het daarbij om gezondheidsgegevens kan gaan, zijn wij wettelijk verplicht u vooraf goed te informeren en uw toestemming te vragen.
                </p>

                {subject != null && (
                  <ConsentItem title={`Ik mag namens ${subject.first_name} toestemming geven`} checked={consentOnBehalfOf} onChange={setConsentOnBehalfOf}>
                    Ik bevestig dat ik bevoegd ben om namens de betrokkene toestemming te geven voor de verwerking van diens persoonsgegevens en aanvaard de privacyverklaring in dit verband.
                    Indien ik optreed als ouder of wettelijke vertegenwoordiger van een minderjarige, verklaar ik dat ik over de vereiste ouderlijke of wettelijke bevoegdheid beschik om deze toestemming te geven.
                  </ConsentItem>
                )}
                <ConsentItem title="Dit is geen spoedgeval" checked={consentNoEmergency} onChange={setConsentNoEmergency}>
                  Ik bevestig dat ik Dermalink niet gebruik voor een dringende of levensbedreigende situatie, begrijp dat dit een beoordeling op afstand is
                  (geen vervanging van een fysieke raadpleging), en bij twijfel over de urgentie onmiddellijk hulp zoek in plaats van op een antwoord te wachten.
                </ConsentItem>
                <ConsentItem title="Geen terugbetaling door ziekenfonds of RIZIV" checked={consentNoReimbursement} onChange={setConsentNoReimbursement}>
                  Ik bevestig dat ik begrijp dat deze consultatie niet wordt terugbetaald door het ziekenfonds of het RIZIV.
                </ConsentItem>
                <ConsentItem
                  title={subject != null ? `Toestemming voor verwerking van ${possessive(subject.first_name)} gezondheidsgegevens` : 'Toestemming voor verwerking van mijn gezondheidsgegevens'}
                  checked={consentHealthData}
                  onChange={setConsentHealthData}
                >
                  {subject != null ? (
                    <>
                      Ik bevestig dat ik uitdrukkelijk toestemming geef voor de verwerking van de gezondheidsgegevens van de betrokkene (foto&apos;s en klinische informatie) door de behandelende dermatoloog, of een andere arts van het team van {selectedDoctor.organization_name},
                      om de betrokkene te behandelen en te adviseren (incl. een advies op afstand), en om deze gegevens desgevallend toe te voegen aan het medisch dossier van de betrokkene bij de praktijk.
                    </>
                  ) : (
                    <>
                      Ik bevestig dat ik uitdrukkelijk toestemming geef voor de verwerking van mijn gezondheidsgegevens (foto&apos;s en klinische informatie) door de behandelende dermatoloog, of een andere arts van het team van {selectedDoctor.organization_name},
                      om mij te behandelen en te adviseren (incl. een advies op afstand), en om deze gegevens desgevallend toe te voegen aan mijn medisch dossier bij de praktijk.
                    </>
                  )}
                  {' '}Ik kan deze toestemming op elk moment intrekken voor de toekomst.
                </ConsentItem>
                <ConsentItem checked={consentLegalDocs} onChange={setConsentLegalDocs}>
                  Ik aanvaard de <a href={ROUTES.medicalDisclaimer} target="_blank" rel="noopener noreferrer">Medische disclaimer</a> en de{' '}
                  <a href={ROUTES.userAgreement} target="_blank" rel="noopener noreferrer">Gebruikersovereenkomst</a> en heb kennis genomen van het{' '}
                  <a href={ROUTES.privacyPolicy} target="_blank" rel="noopener noreferrer">Privacybeleid</a>.
                </ConsentItem>
              </div>
              {startMutation.isError && (
                <p className="text-muted status-message" role="alert">
                  {startMutation.error instanceof Error && startMutation.error.message
                    ? startMutation.error.message
                    : 'Er is een fout opgetreden. Probeer het opnieuw.'}
                </p>
              )}
              <div className={styles.step4Actions}>
                <Button type="button" variant="ghost" onClick={() => setShowIntakeConsentStep(false)} disabled={startMutation.isPending}>
                  Terug
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  disabled={startMutation.isPending || !allIntakeConsentsChecked}
                  onClick={() =>
                    startMutation.mutate({
                      doctorSub: selectedDoctor.cognito_sub,
                      followUpNoPayment: isFollowUpNoPayment,
                      informedConsent: {
                        no_emergency_confirmation: consentNoEmergency,
                        no_reimbursement_confirmation: consentNoReimbursement,
                        health_data_processing_consent: consentHealthData,
                        legal_docs_confirmation: consentLegalDocs,
                        on_behalf_of_confirmation: consentOnBehalfOf,
                      },
                    })}
                >
                  {startMutation.isPending ? 'Bezig…' : 'Gesprek starten'}
                </Button>
              </div>
            </section>
          )}
        </div>
      </div>
    </AuthPageShell>
  );
}

